
import { useMemo, useCallback } from 'react'
import { type ReactFlowInstance } from '@xyflow/react'
import { useJobStore, type JobRecord } from '@/stores/jobStore'
import type { ActivityItem, ActivityStatus } from '@/components/canvas/activity-panel/activity-panel'

/**
 * Maps job store data to ActivityPanel format and provides handlers.
 *
 * Bridges the gap between the JobRecord data model and the
 * ActivityItem presentation type expected by ActivityPanel.
 */

function mapJobStatusToActivity(status: JobRecord['status']): ActivityStatus {
  switch (status) {
    case 'PENDING':
    case 'RUNNING':
      return 'processing'
    case 'COMPLETED':
      return 'success'
    case 'FAILED':
      return 'error'
    case 'CANCELED':
      return 'idle'
    default:
      return 'idle'
  }
}

function mapJobTypeToNodeType(type: JobRecord['type']): string {
  switch (type) {
    case 'TEXT_TO_IMAGE':
    case 'IMAGE_TO_IMAGE':
    case 'IMAGE_UPSCALE':
    case 'IMAGE_ANALYSIS':
      return 'image'
    case 'VIDEO_GENERATION':
      return 'video'
    case 'TEXT_GENERATION':
      return 'text'
    case 'AUDIO_GENERATION':
      return 'sound'
    default:
      return 'image'
  }
}

export function jobToActivityItem(job: JobRecord): ActivityItem {
  return {
    id: job.id,
    title: job.message || `${job.type.replace(/_/g, ' ').toLowerCase()}`,
    description: job.submission ? job.message : `${job.status === 'PENDING' ? 'Queued' : job.status === 'RUNNING' ? 'Generating' : job.status === 'COMPLETED' ? 'Completed' : job.status === 'CANCELED' ? 'Canceled' : 'Failed'}${job.progress !== undefined ? ` · ${job.progress}%` : ''}`,
    timestamp: new Date(job.createdAt),
    status: mapJobStatusToActivity(job.status),
    nodeType: mapJobTypeToNodeType(job.type),
    errorMessage: job.error,
  }
}

interface UseCanvasActivityParams {
  canvasRef: React.RefObject<ReactFlowInstance | null>
  onCancelJob?: (jobId: string) => Promise<boolean>
  onRetryJob?: (jobId: string) => Promise<string | null>
}

export function useCanvasActivity({
  canvasRef,
  onCancelJob,
  onRetryJob,
}: UseCanvasActivityParams) {
  const jobs = useJobStore((s) => s.jobs)
  const activeCanvasId = useJobStore((s) => s.activeCanvasId)

  const activities: ActivityItem[] = useMemo(() => {
    return Array.from(jobs.values())
      .filter(job => !activeCanvasId || job.canvasId === activeCanvasId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 20) // Limit to recent 20
      .map(jobToActivityItem)
  }, [jobs, activeCanvasId])

  const hasActiveJobs = useMemo(() => {
    return Array.from(jobs.values()).some(
      (j) => (!activeCanvasId || j.canvasId === activeCanvasId) && (j.status === 'PENDING' || j.status === 'RUNNING')
    )
  }, [jobs, activeCanvasId])

  const handleGoToNode = useCallback(
    (activity: ActivityItem) => {
      if (!canvasRef.current) return

      // Find the job to get nodeId
      const job = jobs.get(activity.id)
      if (!job?.nodeId) return

      const node = canvasRef.current.getNode(job.nodeId)
      if (!node) return

      canvasRef.current.fitView({
        nodes: [node],
        duration: 300,
        padding: 0.5,
      })
    },
    [canvasRef, jobs]
  )

  const handleActivityClick = useCallback(
    (activity: ActivityItem) => {
      // Navigate to the associated node
      handleGoToNode(activity)
    },
    [handleGoToNode]
  )

  return {
    activities,
    hasActiveJobs,
    handleGoToNode,
    handleActivityClick,
  }
}
